class ActiveFacetsScroll extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'prev',
      'next',
      'scroller',
      'fade-prev',
      'fade-next',
    ]
  }

  connectedCallback () {
    this.scrollAmount = 160
    this.isTicking = false
    this.scrollerEl = null

    this.requestTick = this.requestTick.bind(this)
    this.updateScrollState = this.updateScrollState.bind(this)
    this.onPrev = this.onPrev.bind(this)
    this.onNext = this.onNext.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onContentChange = this.onContentChange.bind(this)

    super.connectedCallback()

    this.bindScroller()
    this.observeContent()
    this.observeSize()
    this.requestTick()
  }

  disconnectedCallback () {
    this.teardownObservers()

    super.disconnectedCallback()
    this._hasBeenInitialised = false
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.prev?.exists == true) {
      this.listeners.add(this.els.prev.element, 'click', this.onPrev)
    }

    if (this.els.next?.exists == true) {
      this.listeners.add(this.els.next.element, 'click', this.onNext)
    }

    this.listeners.add(window, 'resize', this.onResize)
  }

  /**
   * @returns {void}
   */
  bindScroller () {
    this.listeners.remove('scroll', this.onScroll)

    if (this.scrollerEl && this.resizeObserver) {
      this.resizeObserver.unobserve(this.scrollerEl)
    }

    const scroller = this.getScroller()
    if (!scroller) return

    this.listeners.add(scroller, 'scroll', this.onScroll, { passive: true })
    this.scrollerEl = scroller

    if (this.resizeObserver) {
      this.resizeObserver.observe(scroller)
    }
  }

  /**
   * @returns {void}
   */
  teardownObservers () {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    this.scrollerEl = null
  }

  /**
   * @returns {void}
   */
  observeContent () {
    const activeFacets = window.BAO.Utils.getElement(
      '[data-collection-filters-external-el="active-facets"]',
      { context: this },
    )
    if (!activeFacets) return

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    this.mutationObserver = new MutationObserver(this.onContentChange)

    this.mutationObserver.observe(activeFacets, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-empty'],
    })
  }

  /**
   * @returns {void}
   */
  observeSize () {
    if (typeof ResizeObserver === 'undefined') return

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    this.resizeObserver = new ResizeObserver(this.requestTick)
    this.resizeObserver.observe(this)

    const scroller = this.getScroller()
    if (scroller) {
      this.resizeObserver.observe(scroller)
    }
  }

  /**
   * @returns {Element|null}
   */
  getScroller () {
    if (this.els.scroller?.exists != true) return null

    return this.els.scroller.element
  }

  /**
   * @returns {void}
   */
  onContentChange () {
    this.bindScroller()
    this.requestTick()
  }

  /**
   * @returns {void}
   */
  onScroll () {
    this.requestTick()
  }

  /**
   * @returns {void}
   */
  onResize () {
    this.requestTick()
  }

  /**
   * @returns {void}
   */
  onPrev () {
    const scroller = this.getScroller()
    if (!scroller) return

    scroller.scrollBy({
      left: -this.scrollAmount,
      behavior: 'smooth',
    })
  }

  /**
   * @returns {void}
   */
  onNext () {
    const scroller = this.getScroller()
    if (!scroller) return

    scroller.scrollBy({
      left: this.scrollAmount,
      behavior: 'smooth',
    })
  }

  /**
   * @returns {void}
   */
  requestTick () {
    if (this.isTicking == true) return

    this.isTicking = true

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isTicking = false
        this.updateScrollState()
      })
    })
  }

  /**
   * @returns {void}
   */
  updateScrollState () {
    const scroller = this.getScroller()
    if (!scroller) return

    if (scroller.clientWidth === 0) {
      this.toggleControl(this.els.prev?.element, false)
      this.toggleControl(this.els.next?.element, false)
      this.toggleFade(this.els['fade-prev']?.element, false)
      this.toggleFade(this.els['fade-next']?.element, false)
      return
    }

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
    const canScroll = maxScrollLeft > 1
    const atStart = scroller.scrollLeft <= 1
    const atEnd = scroller.scrollLeft >= maxScrollLeft - 1

    this.toggleControl(
      this.els.prev?.element,
      canScroll == true && atStart == false,
    )
    this.toggleControl(
      this.els.next?.element,
      canScroll == true && atEnd == false,
    )
    this.toggleFade(
      this.els['fade-prev']?.element,
      canScroll == true && atStart == false,
    )
    this.toggleFade(
      this.els['fade-next']?.element,
      canScroll == true && atEnd == false,
    )
  }

  /**
   * @param {HTMLElement|undefined} element
   * @param {boolean} isVisible
   *
   * @returns {void}
   */
  toggleControl (element, isVisible) {
    if (!element) return

    if (isVisible == true) {
      element.removeAttribute('hidden')
    } else {
      element.setAttribute('hidden', '')
    }
  }

  /**
   * @param {HTMLElement|undefined} element
   * @param {boolean} isVisible
   *
   * @returns {void}
   */
  toggleFade (element, isVisible) {
    if (!element) return

    element.classList.toggle('is-visible', isVisible == true)
  }
}

if (!customElements.get('active-facets-scroll')) {
  customElements.define('active-facets-scroll', ActiveFacetsScroll)
}
